/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { getDocumentId } from "analytics/interface";
import { getBrowser } from "dc-preview-utils/src/Utils/UserAgentUtils";
import { getOS, getDeviceInfo } from "dc-preview-utils/src/Utils/DeviceUtils";
import { logging as loggingApi, auth2 as auth, providers } from "dc-core";
import * as stores from "stores";
import docApi from "api";
import { getEnableCNPDF, getShouldUseModernViewer } from "selectors/appSelectors";
import { getFileStorageType, getFileShareType, getIsLpmEnabled } from "selectors/documentSelectors";
import { SING_ASSET } from "constants/constants";
import { isSignAsset } from "utils/AssetUtils";
import { getConnectorType } from "app/AppInitUtility";

let documentId = "";

let logger = loggingApi.getLogger({ context: "dc-preview-dropin", dropinId: "dc-preview-dropin" });

export const isAdobeNetworkUser = () => {
  const adobeExternal = "adobe_external";
  const metaTag = document.querySelectorAll(`meta[name^=${adobeExternal}]`);
  const isAdobeNetworkUser = !(metaTag ? (metaTag[0] ? metaTag[0].content : true) : true);
  return isAdobeNetworkUser;
};

export const getConnectionInfo = () => {
  const connection = { speed: "unknown" };
  if (window.navigator && window.navigator.connection && window.navigator.connection.effectiveType) {
    connection.speed = window.navigator.connection.effectiveType;
  }
  return connection;
};

export const getHardwareConcurrency = () => {
  let hardwareConcurrency = "unknown";
  if (window.navigator && window.navigator.hardwareConcurrency) {
    hardwareConcurrency = window.navigator.hardwareConcurrency;
  }
  return hardwareConcurrency;
};

export const getDeviceMemory = () => {
  let deviceMemory = "unknown";
  if (window.navigator && window.navigator.deviceMemory) {
    deviceMemory = window.navigator.deviceMemory;
  }
  return deviceMemory;
};

export const getSharedFileUserType = async () => {
  if (!providers.integrationProvider) {
    return undefined;
  }

  const integrationFactory = await providers.integrationProvider();
  const assets = integrationFactory.getAssetInfo();

  if (!assets) {
    return undefined;
  }

  const { fileObjects, selectedAsset: selectedAssets } = assets;
  let assetInfo;
  if (fileObjects?.length) {
    assetInfo = fileObjects[0];
  } else if (selectedAssets?.length) {
    assetInfo = selectedAssets[0];
  }
  const isOwner = !!assetInfo?.privileges?.owner;
  return isOwner ? "owner" : "reviewer";
};

export const getErrorBoundaryLogger = (addModernViewerFlag = false) => {
  const payload = { context: "ErrorBoundary", dropinId: "dc-preview-dropin" };
  payload.user_agent = window && window.navigator && window.navigator.userAgent;
  payload.isAdobeNetworkUser = isAdobeNetworkUser();
  payload.isLpmEnabled = getIsLpmEnabled(stores);
  if (addModernViewerFlag) {
    payload["adb.event.context.AcrobatJS.isModernViewer"] = getShouldUseModernViewer(stores);
  }
  payload.documentId = documentId;
  return loggingApi.getLogger(payload);
};

export const logErrorBoundaryInfo = (message, ...args) => {
  args[0] = addSystemInfo(args[0]);
  args[0].isAdobeNetworkUser = isAdobeNetworkUser();
  args[0].isLpmEnabled = getIsLpmEnabled(stores);
  getDocumentId()
    .then(hashValue => {
      documentId = hashValue;
    })
    .catch(() => {
      documentId = "";
    })
    .then(() => {
      if (args[0] instanceof Object && documentId) {
        args[0].documentId = documentId;
      }
      getErrorBoundaryLogger(true).info(message, ...args);
    });
};

const addSystemInfo = obj => {
  if (obj instanceof Object) {
    const systemInfo = {
      browser: getBrowser(),
      os: getOS().name,
      device: getDeviceInfo()
    };
    obj = {
      ...obj,
      ...systemInfo
    };
    return obj;
  }
};

export const logInfoToProvider = (message, ...args) => {
  let fileStorageType = getFileStorageType(stores);
  let fileShareType = getFileShareType(stores);
  const _isSignAsset = isSignAsset(docApi.getAssetType());
  const connectorType = getConnectorType();

  if (_isSignAsset) {
    fileStorageType = "sign";
    fileShareType = SING_ASSET;
  } else if (connectorType) {
    fileStorageType = connectorType;
  }
  args[0] = addSystemInfo(args[0]);
  args[0].isAdobeNetworkUser = isAdobeNetworkUser();
  args[0].isLpmEnabled = getIsLpmEnabled(stores);
  getDocumentId()
    .then(hashValue => {
      documentId = hashValue;
    })
    .catch(() => {
      documentId = "";
    })
    .then(() => {
      if (args[0] instanceof Object) {
        if (documentId) {
          args[0].documentId = documentId;
        }
        args[0].sessionId = window.dcWebSessionId;
        args[0].docSessionId = window.docSessionId;
        args[0].isModernViewer = getShouldUseModernViewer(stores);
        args[0].connectionInfo = getConnectionInfo();
        args[0].hardwareConcurrency = getHardwareConcurrency();
        args[0].deviceMemory = getDeviceMemory();
        if (auth) {
          args[0].isSignedIn = auth.isSignedIn;
        }
        if (fileStorageType) {
          args[0].fileStorage = fileStorageType;
        }
        if (fileShareType) {
          args[0].shareType = fileShareType;
        }
        args[0].isCNPDF = getEnableCNPDF(stores) && (fileShareType === "review" || fileShareType === "track");
        // Use logger emit
        if (args[0].loggerEmit === true) {
          delete args[0].loggerEmit;
          logger.emit(message, ...args);
          return;
        }
      }
      logger.info(message, ...args);
    });
};

// Logs arg parallel to message, in splunk, instead
// of as a nested object or a string, against it.
export const emitLogToProvider = (message, arg) => {
  const args = { ...arg, loggerEmit: true };
  logInfoToProvider(message, args);
};

export const setDocIdForLoggingApi = docId => {
  documentId = docId;
};

export const resetDocIdForLoggingApi = docId => {
  documentId = "";
};
